import { Button, ButtonGroup, ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper } from "@mui/material";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import {
  cancelConvertPointCloud,
  convertPointCloud,
  getConvertPointCloudProgress,
  getConvertPointCloudResult
} from "../../../api/point-cloud-api.js";
import { saveFile } from "../../../helpers/file-saver.js";
import { FullscreenDownloadProgress } from "../../ui/fullscreen-download-progress.jsx";
import { useMutation } from "@tanstack/react-query";

const formats = [
  { name: "ASCII Cloud Format (xyz)", format: "xyz" },
  { name: "ASCII Cloud Format (csv)", format: "csv" },
  { name: "Polygon File Format (ply)", format: "ply" },
  { name: "LASer formátum Format (las)", format: "las" },
  { name: "LASer formátum Format (laz)", format: "laz" },
  { name: "Universal 3D Format (u3d)", format: "u3d" },
  { name: "3D PDF Format (pdf)", format: "pdf" },
  { name: "ASTM Format (e57)", format: "e57" },
  { name: "Oracle Point Cloud (oc3)", format: "oc3" },
  { name: "Leica Geosystems PTS (pts)", format: "pts" },
  { name: "AutoCAD DXF (dxf)", format: "dxf" },
  { name: "Point Cloud Data (pcd)", format: "pcd" },
  { name: "Wavefront Object (obj)", format: "obj" }
];

export function PointCloudDownload({ project }) {
  const { t } = useTranslation(["project", "common"]);

  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };
  const timerId = useRef();
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    return () => {
      if (timerId.current) clearTimeout(timerId.current);
    };
  }, []);

  const abortController = useRef();
  const downloadMutation = useMutation({
    mutationFn: async (format) => {
      abortController.current = new AbortController();
      setProgress(10);
      const startConversionRes = await convertPointCloud(project.id, format);
      const jobId = startConversionRes.data.jobId;
      if (!jobId) throw new Error("Unable to start job.");
      // poll progress
      await new Promise((resolve, reject) => {
        function startTimer() {
          timerId.current = setTimeout(() => {
            getConvertPointCloudProgress(project.id, jobId)
              .then((res) => {
                if (res.data.status === "completed") {
                  resolve();
                } else {
                  if (abortController.current.signal.aborted) {
                    cancelConvertPointCloud(project.id, jobId).catch(console.log);
                    reject("Aborted.");
                  } else {
                    if (res.data.progress !== undefined) {
                      setProgress(res.data.progress * 0.8 + 10); // 10-90
                    }
                    startTimer();
                  }
                }
              })
              .catch((e) => {
                reject(e);
              });
          }, 1000);
        }

        startTimer();
      });
      const res = await getConvertPointCloudResult(project.id, jobId, {
        onDownloadProgress: progressEvent => {
          if (progressEvent.progress !== undefined) {
            setProgress(progressEvent.progress * 10 + 90); // 90-100
          }
        }
      });
      saveFile(res.data, `${project.name}.${format}`);
    }
  });

  const download = (format) => {
    setOpen(false);
    downloadMutation.mutate(format);
  };

  const cancel = () => {
    abortController.current?.abort()
  };

  const handleClose = (event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target)
    ) {
      return;
    }
    setOpen(false);
  };

  return (
    <>
      <ButtonGroup
        variant="contained"
      >
        <Button
          variant="outlined"
          onClick={() => download("laz")}
        >
          {t("pointCloud")} (laz)
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={handleToggle}
          ref={anchorRef}
        >
          <ArrowDropDownIcon/>
        </Button>{" "}
      </ButtonGroup>
      <Popper
        sx={{ zIndex: 1 }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement={"bottom-start"}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: "center top",
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList autoFocusItem>
                  {formats.map(f => (
                    <MenuItem
                      key={f.format}
                      onClick={() => download(f.format)}
                    >
                      {f.name}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
      {downloadMutation.isPending && <FullscreenDownloadProgress
        progress={progress}
        onCancel={cancel}
        title={"Exportálás folyamatban..."}
      />}
    </>
  );
}